/** Datums zijn altijd 'YYYY-MM-DD' (kalenderdag, geen tijdzone); tijden 'HH:MM'. */
export type ISODate = string;
export type HHMM = string;

export type Rol = 'BEHEERDER' | 'PLANNER' | 'PROJECTMANAGER' | 'ENGINEER';
export type Inzet = 'VOLLEDIG' | 'NIET_SUGGESTIE' | 'NIET_CAPACITEIT';
export type Startlocatie = 'THUIS' | 'KANTOOR';
export type TaakType = 'PROJECT' | 'SERVICE_CALL' | 'OVERLEG' | 'FOCUS';
export type TaakStatusDb = 'GEPLAND' | 'NIET_GEPLAND';
export type Werkvorm = 'REMOTE' | 'ONSITE';
export type Bron = 'APP' | 'AGENDA';
export type Terugmelding = 'KLAAR' | 'NIET_KLAAR' | 'HERPLANNEN';
export type ProjectStatus = 'OFFERTE' | 'OPDRACHT' | 'GEPLAND' | 'IN_UITVOERING' | 'ON_HOLD' | 'AFGEROND';
export type AfwezigheidSoort = 'VERLOF' | 'ZIEK' | 'CURSUS' | 'OVERIG';

export interface EngineerExpertise { expertiseId: string; naam: string; niveau: number }

export interface Engineer {
  id: string;
  naam: string;
  email: string;
  functie?: string | null;
  werkdagen: string; // 'ma,di,wo,do,vr'
  urenPerDag: number;
  inzet: Inzet;
  startlocatie: Startlocatie;
  adres?: string | null;
  lat?: number | null;
  lon?: number | null;
  actief?: boolean;
  expertises: EngineerExpertise[];
}

export interface TaakExpertise { naam: string; minNiveau: number }

export interface Taak {
  id: string;
  titel: string;
  projectId?: string | null;
  projectnummer?: string | null;
  klant?: string | null;
  taakType: TaakType;
  status: TaakStatusDb;
  engineerId?: string | null;
  startdatum?: ISODate | null;
  einddatum?: ISODate | null;
  van?: HHMM | null;
  tot?: HHMM | null;
  dagen: number;
  urenPerDag?: number | null;
  urenLaatsteDag?: number | null;
  werkvorm: Werkvorm;
  reisMin?: number | null;
  reisKm?: number | null;
  afwAdres?: string | null;
  afwLat?: number | null;
  afwLon?: number | null;
  bron: Bron;
  outlookMelding?: string | null;
  terugmelding?: Terugmelding | null;
  tmAfgehandeld?: boolean;
  takenpakketId?: string | null;
  werkpakketId?: string | null;
  taakCode?: string | null;
  changeNr?: string | null;
  expertises?: TaakExpertise[];
}

export interface Afwezigheid {
  id: string;
  engineerId: string;
  soort: AfwezigheidSoort;
  van: ISODate;
  tot: ISODate;
  vanTijd?: HHMM | null;
  totTijd?: HHMM | null;
  opmerking?: string | null;
}

export interface Feestdag {
  id: string;
  naam: string;
  datum: ISODate;
  vanTijd?: HHMM | null;
  totTijd?: HHMM | null;
}

export interface ProjectExpertiseBudget { naam: string; uren: number }

export interface Project {
  id: string;
  nummer: string;
  naam: string;
  klant?: string | null;
  status: ProjectStatus;
  start?: ISODate | null;
  oplevering?: ISODate | null;
  livegang?: ISODate | null;
  urenBudget?: number | null;
  lat?: number | null;
  lon?: number | null;
  pmEmail?: string | null;
  pmNaam?: string | null;
  expertiseBudget: ProjectExpertiseBudget[];
}

export interface Werkpakket {
  id: string;
  projectId: string;
  naam: string;
  deadline?: ISODate | null;
  volgorde: number;
  afhankelijkVanId?: string | null;
  /** SPEC §9 */
  werkwijze?: 'KLASSIEK' | 'AGILE';
  sprintWeken?: number;
  focusfactor?: number;
  standup?: boolean;
  schatting?: 'UREN' | 'PUNTEN';
  focusUren?: number;
}

export interface Takenpakket {
  id: string;
  projectId: string;
  werkpakketId?: string | null;
  naam: string;
  expertise?: string | null;
  uren: number;
  minNiveau: number;
  maxNiveau: number;
  afhankelijkVanId?: string | null;
  volgorde: number;
  /** SPEC §9 (agile werkpakket) */
  status?: 'BACKLOG' | 'GEPLAND' | 'BEZIG' | 'REVIEW' | 'KLAAR';
  sprintId?: string | null;
  eigenaarId?: string | null;
  afgerondOp?: string | null;
  punten?: number | null;
  /** SPEC §11 */
  restUren?: number | null;
  besteedMin?: number | null;
}

export interface Instellingen {
  kennisWeging: number;
  bonusProject: number;
  aantalSuggesties: number;
  taakVan: HHMM;
  taakTot: HHMM;
  pauzeMin: number;
  pauzeVanaf: number;
  engUren: number;
  engDagen: string;
  outlookVan: HHMM;
  outlookTot: HHMM;
  taakcodes: string; // regels "1015 - Installatie onsite"
  kantoorAdres?: string | null;
  kantoorLat?: number | null;
  kantoorLon?: number | null;
  reisFactor: number;
  reisSnelheid: number;
  reisOpslag: number;
  eigenReistijd: number;
  prognoseMinUren: number;
  prognoseMaxEngineers: number;
  prognoseInzet: number;
  urenlinkSjabloon?: string | null;
  topdeskSjabloon?: string | null;
}

export const STANDAARD_INSTELLINGEN: Instellingen = {
  kennisWeging: 40,
  bonusProject: 15,
  aantalSuggesties: 6,
  taakVan: '08:00',
  taakTot: '16:30',
  pauzeMin: 30,
  pauzeVanaf: 5.5,
  engUren: 8,
  engDagen: 'ma,di,wo,do,vr',
  outlookVan: '08:00',
  outlookTot: '17:00',
  taakcodes: '1014 - Remote\n1015 - Installatie onsite\n8734 - Service call remote\n8735 - Service call onsite',
  kantoorAdres: null,
  kantoorLat: null,
  kantoorLon: null,
  reisFactor: 1.3,
  reisSnelheid: 70,
  reisOpslag: 10,
  eigenReistijd: 0,
  prognoseMinUren: 2,
  prognoseMaxEngineers: 2,
  prognoseInzet: 100,
  urenlinkSjabloon: null,
  topdeskSjabloon: null,
};

/**
 * Outlook-beschikbaarheid: per engineer per dag een string van 96 tekens (vakken van 15 minuten, 00:00–24:00).
 * 0 = vrij, 1 = voorlopig, 2 = bezet, 3 = afwezig (oof), 4 = elders.
 */
export type BezetMap = Map<string, Map<ISODate, string>>;
